<template>
  <v-data-table
    :headers="computedHeaders"
    :items="tasks"
    :loading="loading"
    :search="search"
    :class="{ 'row-hover': clickable }"
    dense
    disable-pagination
    hide-default-footer
    sort-by="date"
    @click:row="handleRowClick"
  >
    <template v-slot:item.title="{ item }">
      <div>
        <strong>{{ item.title || 'Untitled Task' }}</strong>
        <div v-if="isRateOutlier(item) || isDurationOutlier(item)" class="outlier-indicator">
          <v-icon class="mr-1" color="orange" small>mdi-alert-circle</v-icon>
          <span class="caption orange--text">
            <span v-if="isRateOutlier(item)">Rate Outlier</span>
            <span v-if="isRateOutlier(item) && isDurationOutlier(item)"> & </span>
            <span v-if="isDurationOutlier(item)">Duration Outlier</span>
          </span>
        </div>
      </div>
    </template>
    <template v-slot:item.team="{ item }">
      {{ getTaskTeam(item) }}
    </template>
    <template v-slot:item.date="{ item }">
      {{ formatDate(item.from) }}-{{ formatDate(item.to) }}
    </template>
    <template v-slot:item.executionTimes="{ item }">
      <div v-if="item.execution && item.execution.length" style="font-size: small; white-space: nowrap;">
        <div v-for="(exec, idx) in item.execution" :key="idx">
          {{ formatExecTime(exec.start) }} - {{ formatExecTime(exec.stop) }} ({{formatDate(exec.start)}})
        </div>
      </div>
      <span v-else>N/A</span>
    </template>
    <template v-slot:item.memberCount="{ item }">
      {{ getTaskMemberCount(item) }}
    </template>
    <template v-slot:item.memberNames="{ item }">
      <div style="font-size: small">{{ getTaskMemberNames(item).join(', ') }}</div>
    </template>
    <template v-slot:item.duration="{ item }">
      <span :class="{ 'orange--text': isDurationOutlier(item) }">
        {{ formatDuration(item.elapsed) }}
        <v-icon v-if="isDurationOutlier(item)" class="ml-1" color="orange" small>
          {{ getDurationArrowIcon(item) }}
        </v-icon>
      </span>
    </template>
    <template v-slot:item.totalWeight="{ item }">
      {{ formatWeight(calculateTotalWeight(item.description)) }}
    </template>
    <template v-slot:item.rate="{ item }">
      <span :class="{ 'orange--text': isRateOutlier(item) }">
        {{ formatRate(calculateRate(item.description, item.elapsed)) }}
        <v-icon v-if="isRateOutlier(item)" class="ml-1" color="orange" small>
          {{ getRateArrowIcon(item) }}
        </v-icon>
      </span>
    </template>
    <template v-slot:item.description="{ item }">
      <div v-for="(line,i) of item.description.split('\n')" v-bind:key="i" style="font-size: small">
        {{ line }}
      </div>
    </template>
    <template v-slot:item.relationReason="{ item }">
      <v-chip color="green" small>{{ item.relationReason }}</v-chip>
    </template>
    <!-- Additional slots for main task table -->
    <template v-slot:item.source="{ item }">
      {{ item.source ? item.source.foodid || 'N/A' : 'N/A' }}
    </template>
    <template v-slot:item.apiRate="{ item }">
      <div class="d-flex align-center">
        <span>{{ item.apiRate || '' }}</span>
        <v-icon 
          v-if="item.entryType === 'manual-update'" 
          color="blue" 
          small 
          class="ml-1"
          title="Manually set rate"
        >
          mdi-check-circle
        </v-icon>
      </div>
    </template>
    <template v-slot:item.updatedRate="{ item }">
      {{ item.updatedRate || '' }}
    </template>
    <!-- Additional slots for TaskReport2 task detail table -->
    <template v-slot:item.estimatedDuration="{ item }">
      {{ getTaskEstimatedDuration(item) || 'N/A' }}
    </template>
    <template v-slot:item.estimatedRate="{ item }">
      {{ item.estimatedRate || 'N/A' }}
    </template>
    <template v-slot:item.performance="{ item }">
      <v-chip
        v-if="getTaskPerformance(item)"
        :color="getTaskPerformance(item).color"
        text-color="white"
        small
      >
        {{ getTaskPerformance(item).text }}
      </v-chip>
      <span v-else>N/A</span>
    </template>
    <template v-slot:item.actions="{ item }">
      <slot name="actions" :item="item">
        <!-- Default actions slot content -->
      </slot>
    </template>
  </v-data-table>
</template>

<script>
import moment from 'moment';
import {
  calculateTotalWeight,
  formatWeight,
  calculateRate,
  formatRate,
  formatDuration,
  calculateEstimatedDuration,
  calculatePerformance
} from '@/utils/tasks/chopping-utils.js';

export default {
  name: 'ChoppingTaskTable',
  props: {
    tasks: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    teams: {
      type: Object,
      default: () => ({})
    },
    headers: {
      type: Array,
      default: null
    },
    outlierBounds: {
      type: Object,
      required: false,
      default: () => ({ rateBounds: { lower: 0, upper: 0 }, durationBounds: { lower: 0, upper: 0 } }),
      validator: (bounds) => {
        return bounds.rateBounds && bounds.durationBounds &&
               typeof bounds.rateBounds.lower === 'number' &&
               typeof bounds.rateBounds.upper === 'number' &&
               typeof bounds.durationBounds.lower === 'number' &&
               typeof bounds.durationBounds.upper === 'number';
      }
    },
    outlierFilterMode: {
      type: String,
      default: 'strict',
      validator: (value) => ['strict', 'standard', 'none'].includes(value)
    },
    clickable: {
      type: Boolean,
      default: false
    },
    search: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      defaultHeaders: [
        {text: 'Task', value: 'title'},
        {text: 'Date', value: 'date'},
        {text: 'Task Times', value: 'executionTimes'},
        {text: 'Members', value: 'memberCount'},
        {text: 'Member Names', value: 'memberNames'},
        {text: 'Duration', value: 'duration'},
        {text: 'Total Weight', value: 'totalWeight'},
        {text: 'Rate (kg/hr)', value: 'rate'},
        {text: 'Description', value: 'description'}
      ]
    };
  },
  computed: {
    computedHeaders() {
      return this.headers || this.defaultHeaders;
    }
  },
  methods: {
    // Expose utility functions
    calculateTotalWeight,
    formatWeight,
    calculateRate,
    formatRate,
    formatDuration,

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('MMM D, YYYY');
    },
    
    formatExecTime(dateTime) {
      if (!dateTime) return '—';
      return moment(dateTime).format('HH:mm');
    },

    getTaskTeam(task) {
      if (task.execution && task.execution.length > 0) {
        for (const execution of task.execution) {
          if (execution.team_members && execution.team_members.length > 0) {
            const teamId = execution.team_members[0].team_id;
            return this.teams[teamId] || teamId;
          }
        }
      }
      return 'Unknown';
    },

    getTaskMemberNames(task) {
      const namesSet = new Set();
      if (task && task.execution && task.execution.length > 0) {
        for (const execution of task.execution) {
          if (execution.team_members && execution.team_members.length > 0) {
            for (const member of execution.team_members) {
              if (member && member.name) {
                namesSet.add(member.name);
              }
            }
          }
        }
      }
      return Array.from(namesSet);
    },

    getTaskMemberCount(task) {
      return this.getTaskMemberNames(task).length;
    },

    isRateOutlier(task) {
      if (this.outlierFilterMode === 'none') return false;
      
      const bounds = this.outlierBounds.rateBounds;
      if (bounds.lower === 0 && bounds.upper === 0) return false; // No valid stats

      const taskRate = calculateRate(task.description, task.elapsed);
      return taskRate < bounds.lower || taskRate > bounds.upper;
    },

    isDurationOutlier(task) {
      if (this.outlierFilterMode === 'none') return false;
      
      const bounds = this.outlierBounds.durationBounds;
      if (bounds.lower === 0 && bounds.upper === 0) return false; // No valid stats

      const taskDuration = task.elapsed / (1000 * 60 * 60); // Convert to hours
      return taskDuration < bounds.lower || taskDuration > bounds.upper;
    },

    getDurationArrowIcon(task) {
      if (!this.isDurationOutlier(task)) return '';

      const bounds = this.outlierBounds.durationBounds;
      const taskDuration = task.elapsed / (1000 * 60 * 60);

      return taskDuration > bounds.upper ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },

    getRateArrowIcon(task) {
      if (!this.isRateOutlier(task)) return '';

      const bounds = this.outlierBounds.rateBounds;
      const taskRate = calculateRate(task.description, task.elapsed);

      return taskRate > bounds.upper ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },

    getTaskEstimatedDuration(task) {
      // Calculate estimated duration based on weight and API rate
      if (!task.apiRate) return null;
      
      const totalWeightKg = calculateTotalWeight(task.description) / 1000;
      const estimatedRateNum = parseFloat(task.apiRate);
      
      if (!totalWeightKg || !estimatedRateNum) return null;
      
      const estimatedDurationHours = calculateEstimatedDuration(totalWeightKg, estimatedRateNum);
      if (!estimatedDurationHours) return null;
      
      // Convert hours to milliseconds and format
      return formatDuration(estimatedDurationHours * 1000 * 60 * 60);
    },

    getTaskPerformance(task) {
      // Calculate performance by comparing actual vs estimated duration
      if (!task.apiRate || !task.elapsed) return null;
      
      const totalWeightKg = calculateTotalWeight(task.description) / 1000;
      const actualDurationHours = task.elapsed / (1000 * 60 * 60);
      const estimatedRateNum = parseFloat(task.apiRate);
      
      if (!totalWeightKg || !estimatedRateNum) return null;
      
      const estimatedDurationHours = calculateEstimatedDuration(totalWeightKg, estimatedRateNum);
      if (!estimatedDurationHours) return null;

      // console.log('compare', task.title,actualDurationHours,estimatedDurationHours);
      return calculatePerformance(actualDurationHours, estimatedDurationHours);
    },

    handleRowClick(item, event) {
      if (this.clickable) {
        this.$emit('row-click', item, event);
      }
    }
  }
};
</script>

<style scoped>
.outlier-indicator {
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.row-hover tbody tr:hover {
  background-color: #f5f5f5 !important;
  cursor: pointer;
}
</style>